import { __ } from '@wordpress/i18n';
import { useContext, useState, useRef, useEffect } from 'react';
import Select from '../../../Common/Select';
import SettingsScreenContext from '../../../Context/SettingsScreenContext';
import './ManageGDPR.css';

const ManageGDPR = () => {
    const {
        settingsPage,
        sbCustomizer
    } = useContext(SettingsScreenContext);

    const gdprInfo = sbCustomizer?.gdprInfo || {};
    const activePlugin = gdprInfo?.activePlugin || false;
    const gdprTexts = gdprInfo?.texts || {};

    const currentValue = settingsPage?.pluginSettings?.gdpr || 'auto';
    const [showLimited, setShowLimited] = useState(false);
    const tooltipRef = useRef(null);
    const triggerRef = useRef(null);

    const handleChange = (event) => {
        setShowLimited(false);
        settingsPage?.setPluginSettings({
            ...settingsPage?.pluginSettings,
            gdpr: event.currentTarget.value
        });
    };

    // Close tooltip when clicking outside.
    useEffect(() => {
        if (!showLimited) return;
        const handleClickOutside = (e) => {
            if (
                tooltipRef.current && !tooltipRef.current.contains(e.target) &&
                triggerRef.current && !triggerRef.current.contains(e.target)
            ) {
                setShowLimited(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showLimited]);

    const renderWhatLimited = () => {
        return (
            <span
                ref={triggerRef}
                className='sb-gdpr-what-limited sb-text-bold'
                onClick={(e) => {
                    e.preventDefault();
                    setShowLimited(!showLimited);
                }}
            >
                {gdprTexts?.whatLimited || __('What will be limited?', 'sb-customizer')}
            </span>
        );
    };

    const renderLimitedTooltip = () => {
        const features = gdprTexts?.limitedFeatures || [];
        if (!showLimited || features.length === 0) {
            return null;
        }

        return (
            <div className='sb-gdpr-info-tooltip' ref={tooltipRef}>
                <span className='sb-gdpr-info-headline'>
                    {gdprTexts?.limitedHeadline || __('Features that would be disabled or limited include:', 'sb-customizer')}
                </span>
                <ul className='sb-gdpr-info-list'>
                    {features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                    ))}
                </ul>
            </div>
        );
    };

    const renderAutoInfo = () => {
        if (activePlugin) {
            return (
                <div className='sb-gdpr-help-text sb-gdpr-active'>
                    <span className='sb-gdpr-active-icon'>
                        <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M10.0003 1.66667C5.41699 1.66667 1.66699 5.41667 1.66699 10C1.66699 14.5833 5.41699 18.3333 10.0003 18.3333C14.5837 18.3333 18.3337 14.5833 18.3337 10C18.3337 5.41667 14.5837 1.66667 10.0003 1.66667ZM8.33366 14.1667L4.16699 10L5.34199 8.82501L8.33366 11.8083L14.6587 5.48334L15.8337 6.66667L8.33366 14.1667Z' fill='#59AB46'/>
                        </svg>
                    </span>
                    <div className='sb-text-bold'>{activePlugin}</div>
                    <span>{gdprTexts?.autoActive || __('Some TikTok Feed features will be limited for visitors to ensure GDPR compliance, until they give consent.', 'sb-customizer')}</span>
                    {' '}
                    {renderWhatLimited()}
                </div>
            );
        }

        return (
            <div className='sb-gdpr-help-text'>
                <span dangerouslySetInnerHTML={{ __html: gdprTexts?.autoInactive || __('No GDPR consent plugin detected.', 'sb-customizer') }} />
            </div>
        );
    };

    const renderYesInfo = () => {
        return (
            <div className='sb-gdpr-help-text sb-gdpr-help-text-yes'>
                <span>{gdprTexts?.yes || __('No requests will be made to third-party websites. To accommodate this, some features of the plugin will be limited.', 'sb-customizer')}</span>
                {' '}
                {renderWhatLimited()}
            </div>
        );
    };

    const renderNoInfo = () => {
        return (
            <div className='sb-gdpr-help-text'>
                <span>{gdprTexts?.no || __('The plugin will function as normal and load images and videos directly from TikTok.', 'sb-customizer')}</span>
            </div>
        );
    };

    return (
        <div className='sb-gdpr-section'>
            <div className='sb-gdpr-select-wrap'>
                <Select
                    customClass='sb-select-control'
                    size='small'
                    value={currentValue}
                    onChange={handleChange}
                >
                    <option value='auto'>{__('Automatic', 'sb-customizer')}</option>
                    <option value='yes'>{__('Yes', 'sb-customizer')}</option>
                    <option value='no'>{__('No', 'sb-customizer')}</option>
                </Select>
            </div>

            <div className='sb-gdpr-info-wrap'>
                {currentValue === 'auto' && renderAutoInfo()}
                {currentValue === 'yes' && renderYesInfo()}
                {currentValue === 'no' && renderNoInfo()}
                {renderLimitedTooltip()}
            </div>

        </div>
    );
};

export default ManageGDPR;
